/**
 * Tree manipulation and calculation helpers (bounds, etc.)
 */

import type { LayoutNode, TreeBounds, VisualEquipment } from '../types';
import { normalizeEquipmentId } from '../idUtils';

/**
 * Single-pass index: parent equipment id → direct children (real + ghosts).
 * Avoids O(n) filter per node during layout.
 */
export function buildChildrenByParentId(
  visualEquipmentList: VisualEquipment[],
  ghostNodes: VisualEquipment[]
): Map<string, VisualEquipment[]> {
  const map = new Map<string, VisualEquipment[]>();
  const push = (parentId: string | null | undefined, child: VisualEquipment) => {
    if (parentId == null || parentId === '') return;
    const list = map.get(parentId);
    if (list) list.push(child);
    else map.set(parentId, [child]);
  };
  for (const e of visualEquipmentList) {
    push(normalizeEquipmentId(e.parentEquipmentId), e);
  }
  for (const g of ghostNodes) {
    push(normalizeEquipmentId(g.parentEquipmentId ?? g.parentId), g);
  }
  return map;
}

/**
 * Calculate bounds for minimap
 */
export function calculateTreeBounds(layoutNodes: LayoutNode[]): TreeBounds {
  if (layoutNodes.length === 0) {
    return { minX: 0, maxX: 0, minY: 0, maxY: 0, width: 0, height: 0 };
  }

  const xs = layoutNodes.map(n => n.x);
  const ys = layoutNodes.map(n => n.y);

  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);

  return {
    minX,
    maxX,
    minY,
    maxY,
    width: maxX - minX,
    height: maxY - minY
  };
}

/**
 * Virtual string/module rows often have no assignment fields; show the same engineer as the
 * nearest real ancestor that is assigned (combiner, inverter, etc.).
 */
export function applyVirtualAssignmentInheritance(nodes: VisualEquipment[]): VisualEquipment[] {
  const byId = new Map(nodes.map(n => [n.id, n]));
  const cache = new Map<string, VisualEquipment['assignedTo'] | undefined>();

  const assignedFromAncestor = (startParentId: string | null | undefined): VisualEquipment['assignedTo'] | undefined => {
    if (!startParentId) return undefined;
    if (cache.has(startParentId)) return cache.get(startParentId);
    const seen = new Set<string>();
    let pid: string | null = startParentId;
    let found: VisualEquipment['assignedTo'] | undefined;
    while (pid && !seen.has(pid)) {
      seen.add(pid);
      const row = byId.get(pid);
      if (!row) break;
      if (row.isAssigned && row.assignedTo?.name) {
        found = row.assignedTo;
        break;
      }
      pid = row.parentEquipmentId ?? null;
    }
    cache.set(startParentId, found);
    return found;
  };

  return nodes.map(n => {
    if (!n.isVirtual) return n;
    if (n.isAssigned && n.assignedTo?.name) return n;
    const inherited = assignedFromAncestor(n.parentEquipmentId);
    if (!inherited) return n;
    return { ...n, isAssigned: true, assignedTo: inherited };
  });
}

/** Fallback labels when API has id but not yet resolved display name */
const PLACEHOLDER_ASSIGNEE_NAMES = new Set(['Site engineer', 'Site supervisor']);

function isPlaceholderAssigneeName(name: string): boolean {
  return PLACEHOLDER_ASSIGNEE_NAMES.has(name.trim());
}

export type ResourceDisplayEntry = {
  text: string;
  /** True when label comes from descendant equipment (parent row had no concrete name) */
  isInherited: boolean;
  /** All distinct names when multiple assignees under subtree (for tooltip) */
  allNames?: string[];
};

/**
 * For each equipment node, pick a resource label: direct assignment, else union of assignees
 * on this node and all descendants (so PT / inverter / combiner show the same engineer as strings below).
 */
export function buildResourceDisplayByNodeId(
  nodes: VisualEquipment[],
  childrenByParentId: Map<string, VisualEquipment[]>
): Map<string, ResourceDisplayEntry> {
  const byId = new Map(nodes.map(e => [e.id, e]));
  /** node id → map assigneeUserId → display name for self + subtree */
  const unionByNode = new Map<string, Map<string, string>>();

  function mergeSelf(acc: Map<string, string>, eq: VisualEquipment | undefined) {
    if (!eq || eq.isGhost) return;
    if (eq.isAssigned && eq.assignedTo?.id && eq.assignedTo.name) {
      acc.set(eq.assignedTo.id, eq.assignedTo.name);
    }
  }

  function dfs(id: string): Map<string, string> {
    const acc = new Map<string, string>();
    mergeSelf(acc, byId.get(id));
    for (const ch of childrenByParentId.get(id) ?? []) {
      const sub = dfs(ch.id);
      for (const [k, v] of sub) acc.set(k, v);
    }
    unionByNode.set(id, acc);
    return acc;
  }

  const roots = new Set<string>();
  for (const n of nodes) {
    if (n.isGhost) continue;
    const pk = normalizeEquipmentId(n.parentEquipmentId);
    if (!pk || !byId.has(pk)) roots.add(n.id);
  }
  for (const r of roots) {
    dfs(r);
  }

  const out = new Map<string, ResourceDisplayEntry>();
  for (const eq of nodes) {
    if (eq.isGhost) continue;
    const union = unionByNode.get(eq.id) ?? new Map<string, string>();
    const directName = eq.isAssigned && eq.assignedTo?.name ? eq.assignedTo.name : '';
    const directId = eq.isAssigned && eq.assignedTo?.id ? eq.assignedTo.id : '';
    const directReal = directName && !isPlaceholderAssigneeName(directName);

    if (directReal) {
      out.set(eq.id, { text: directName, isInherited: false });
      continue;
    }

    const filtered = new Map(union);
    if (directId && directName && isPlaceholderAssigneeName(directName)) {
      filtered.delete(directId);
    }

    if (filtered.size === 0) {
      if (directName) {
        out.set(eq.id, { text: directName, isInherited: false });
      }
      continue;
    }

    const assigneeIds = [...filtered.keys()].sort();
    const uniqueNames = [...new Set(assigneeIds.map(idKey => filtered.get(idKey)!).filter(Boolean))];
    if (assigneeIds.length === 1) {
      out.set(eq.id, { text: filtered.get(assigneeIds[0])!, isInherited: true });
      continue;
    }
    const firstName = filtered.get(assigneeIds[0]) ?? uniqueNames[0] ?? '';
    out.set(eq.id, {
      text: `${firstName} +${assigneeIds.length - 1}`,
      isInherited: true,
      allNames: uniqueNames.length > 0 ? uniqueNames : [...new Set(filtered.values())]
    });
  }
  return out;
}
